import React, { useState } from "react";
import { Button, Flex, Input, Modal, theme, Typography } from "antd";
import styled from "styled-components";
import { useTranslate } from "@refinedev/core";
import { TrashIcon, WarningIcon, XIcon } from "@phosphor-icons/react";
import modalBg from "@/assets/images/onboarding/modal-background.png";
import { WarningChip, Message } from "./ConfirmRemovalModal.styles";
import { radius } from "@libertify/ui";

/**
 * Which face of the modal is showing. One component rather than three, since
 * the states share the chrome and hand the typed name to one another.
 */
type ModalState = "name" | "confirmDiscard" | "saved";

/** Both faces. Narrower than the welcome modals — this one carries a form. */
const CARD_WIDTH = 520;

interface SavePlaybookModalProps {
  open: boolean;
  /** Pre-filled, editable suggestion — never an empty field to stare at. */
  defaultName: string;
  /** Commits the playbook under `name`, with an optional description. */
  onSave: (name: string, description?: string) => void;
  /** Commits the deletion. Always followed by the modal closing. */
  onDiscard: () => void;
  /** "Done, close me" — after an explicit save, or from the saved state. */
  onAcknowledge: () => void;
  /** The save is in flight (real API, unlike the design prototype). */
  isSaving?: boolean;
}

/**
 * Asks whether to keep the playbook the customize flow just created.
 *
 * Dismissing (✕, backdrop, Escape) SAVES rather than discards: the user built
 * this configuration, so losing it silently is the worse failure. Discarding
 * is therefore explicit and confirmed, and an autosave says so before closing.
 */
export const SavePlaybookModal: React.FC<SavePlaybookModalProps> = ({
  open,
  defaultName,
  onSave,
  onDiscard,
  onAcknowledge,
  isSaving = false,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  const [state, setState] = useState<ModalState>("name");
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState("");

  // Reset on the closed→open transition. Adjusted during render (React's
  // documented pattern) rather than in an effect, so the modal never paints
  // one frame of the previous session's state.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setState("name");
      setName(defaultName);
      setDescription("");
    }
  }

  const trimmed = name.trim();
  const trimmedDescription = description.trim() || undefined;
  const canSave = trimmed.length > 0;

  const commitSave = () => {
    if (!canSave || isSaving) return;
    onSave(trimmed, trimmedDescription);
    onAcknowledge();
  };

  // Dismissing the naming step is an autosave, not a discard — the saved state
  // then tells the user it happened instead of letting it be silent.
  const autoSave = () => {
    const finalName = trimmed || defaultName;
    setName(finalName);
    onSave(finalName, trimmedDescription);
    setState("saved");
  };

  const key = `createExperience.savePlaybookModal.${
    state === "confirmDiscard" ? "discard" : state
  }`;

  // A stray backdrop click mid-confirmation must not read as "yes, delete it".
  const onClose = () => {
    if (state === "confirmDiscard") {
      setState("name");
      return;
    }
    if (state === "saved") {
      onAcknowledge();
      return;
    }
    autoSave();
  };

  const isIllustratedCard = state !== "confirmDiscard";

  const closeButton = (
    <CloseButton
      onClick={onClose}
      aria-label={t("buttons.close")}
      icon={<XIcon size={16} />}
      $floating={isIllustratedCard}
    />
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={CARD_WIDTH}
      styles={{
        content: {
          padding: 0,
          borderRadius: radius.box,
          overflow: "hidden",
          ...(isIllustratedCard && {
            backgroundImage: `url(${modalBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }),
        },
        body: { padding: 0 },
      }}
    >
      {state === "confirmDiscard" ? (
        <>
          <WarnHead align="center" justify="space-between" gap={12}>
            <WarnHeadTitle>{t(`${key}.title`)}</WarnHeadTitle>
            {closeButton}
          </WarnHead>
          <Flex gap={14} style={{ padding: "20px 24px 4px" }}>
            <WarningChip $token={token}>
              <WarningIcon size={20} weight="fill" />
            </WarningChip>
            <Message $token={token}>{t(`${key}.message`)}</Message>
          </Flex>
          <Flex
            justify="flex-end"
            align="center"
            gap={10}
            style={{ padding: "20px 24px 24px" }}
          >
            <GhostButton onClick={() => setState("name")}>
              {t(`${key}.back`)}
            </GhostButton>
            <DiscardButton onClick={onDiscard} icon={<TrashIcon size={16} />}>
              {t(`${key}.confirm`)}
            </DiscardButton>
          </Flex>
        </>
      ) : (
        <>
          {closeButton}
          <HeroBody vertical align="center">
            <Title>
              {state === "saved"
                ? t(`${key}.title`, { name })
                : t(`${key}.title`)}
            </Title>
            <Sub>{t(`${key}.message`)}</Sub>
            {state === "saved" ? (
              <Flex vertical align="center" gap={16} style={{ width: "100%" }}>
                <SaveButton onClick={onAcknowledge}>
                  {t(`${key}.acknowledge`)}
                </SaveButton>
              </Flex>
            ) : (
              <>
                <Field vertical gap={8}>
                  <FieldLabel htmlFor="save-playbook-name">
                    {t(`${key}.nameLabel`)}
                  </FieldLabel>
                  <TextInput
                    id="save-playbook-name"
                    value={name}
                    placeholder={t(`${key}.namePlaceholder`)}
                    onChange={(event) => setName(event.target.value)}
                    onPressEnter={commitSave}
                  />
                </Field>
                <Field vertical gap={8}>
                  <FieldLabel htmlFor="save-playbook-description">
                    {t(`${key}.descriptionLabel`)}
                  </FieldLabel>
                  {/* Optional: no onPressEnter, so newlines stay typable. */}
                  <TextAreaInput
                    id="save-playbook-description"
                    rows={3}
                    value={description}
                    placeholder={t(`${key}.descriptionPlaceholder`)}
                    onChange={(event) => setDescription(event.target.value)}
                  />
                </Field>
                <Flex
                  vertical
                  align="center"
                  gap={16}
                  style={{ width: "100%" }}
                >
                  <SaveButton
                    disabled={!canSave}
                    loading={isSaving}
                    onClick={commitSave}
                  >
                    {t(`${key}.save`)}
                  </SaveButton>
                  <DismissButton
                    type="text"
                    disabled={isSaving}
                    onClick={() => setState("confirmDiscard")}
                  >
                    {t(`${key}.dismiss`)}
                  </DismissButton>
                </Flex>
              </>
            )}
          </HeroBody>
        </>
      )}
    </Modal>
  );
};

// ─── Styled components ───────────────────────────────────────────────────────

const MONO900 = "#090e19";
const MONO700 = "#595d66";
const MONO500 = "#9399a6";
const MONO300 = "#d8e0e3";
const GREEN600 = "#2faaa2";
const GREEN700 = "#247f79";
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

const CloseButton = styled(Button)<{ $floating: boolean }>`
  background: #eef0f2;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  color: ${MONO700};
  flex-shrink: 0;

  ${({ $floating }) =>
    $floating &&
    `
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 1;
  `}

  &:hover {
    background: #eef0f2;
    color: ${MONO900};
  }
`;

const HeroBody = styled(Flex)`
  overflow-y: auto;
  padding: 72px 48px 56px;
  text-align: center;

  @media (width <= 480px) {
    padding: 56px 24px 40px;
  }
`;

const Title = styled(Typography.Title).attrs({ level: 2 as const })`
  && {
    letter-spacing: -0.03em;
    line-height: 1.15;
    margin: 0 0 16px;

    @media (width <= 480px) {
      font-size: 22px;
    }
  }
`;

const Sub = styled.p`
  font-size: 15px;
  letter-spacing: -0.2px;
  color: ${MONO700};
  line-height: 1.6;
  margin: 0 0 28px;
`;

const Field = styled(Flex)`
  width: 100%;
  margin-bottom: 28px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${MONO700};
  text-align: left;
`;

const fieldStyles = `
  font-size: 15px;
  color: ${MONO900};
  border: 1px solid ${MONO300};
  border-radius: 10px;
  padding: 12px 14px;
  background: #fff;
  text-align: left;

  &::placeholder {
    color: ${MONO500};
  }

  &:hover,
  &:focus {
    border-color: ${GREEN600};
    box-shadow: none;
  }
`;

const TextInput = styled(Input)`
  ${fieldStyles}
`;

const TextAreaInput = styled(Input.TextArea)`
  ${fieldStyles}
  resize: vertical;
`;

const PillButton = styled(Button)`
  border-radius: ${radius.pill}px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.28px;
  box-shadow: none;
`;

const SaveButton = styled(PillButton)`
  width: 100%;
  height: 44px;
  padding: 0 22px;
  border: none;
  font-size: 15px;
  background: ${GREEN600};
  color: #fff;

  &:disabled {
    background: ${MONO300};
    color: #fff;
  }

  &:hover:not(:disabled) {
    background: ${GREEN700};
    color: #fff;
  }
`;

const DismissButton = styled(Button)`
  padding: 0;
  height: auto;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.28px;
  color: ${MONO500};
  transition: color 0.15s ${EASE};

  &:hover:not(:disabled) {
    background: transparent;
    color: ${MONO700};
  }
`;

const WarnHead = styled(Flex)`
  padding: 16px 24px;
  border-bottom: 1px solid ${MONO300};
  flex-shrink: 0;
`;

const WarnHeadTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.3px;
  color: ${MONO900};
`;

const GhostButton = styled(PillButton)`
  height: 36px;
  padding: 0 16px;
  border: 1px solid ${MONO300};
  background: #fff;
  color: ${MONO700};

  &:hover:not(:disabled) {
    border-color: ${GREEN600};
    color: ${GREEN700};
  }
`;

const DiscardButton = styled(PillButton)`
  height: 36px;
  padding: 0 18px;
  border: none;
  background: #dc2626;
  color: #fff;

  &:hover:not(:disabled) {
    background: #b91c1c;
    color: #fff;
  }
`;
